import { DOCUMENT } from "@angular/common";
import { Component, DestroyRef, effect, Inject, input, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslationKey } from "@ankaadia/ankaadia-shared";
import { Button } from "@ankaadia/ankaadia-shared-frontend";
import { isNil } from "lodash";
import { PrimeTemplate } from "primeng-v17/api";
import { CarouselModule } from "primeng-v17/carousel";
import { ConfirmDialogModule } from "primeng-v17/confirmdialog";
import { DialogModule } from "primeng-v17/dialog";
import { CarouselPageEvent } from "primeng/carousel";
import { map } from "rxjs";
import { OrganizationFactoryService } from "../../organization-specific/organization-specific-factory.service";
import { SafePipe } from "../../shared/pipes/safe.pipe";
import { SettingsService } from "../../shared/services/settings.service";
import { TranslateDirective } from "../../shared/transloco/translate.directive";
import { UserEnvironmentService } from "../user-environment/user-environment.service";
import { NewAnkaadiaAnnouncementHeaderComponent } from "./new-ankaadia-announcement-header.component";

interface AnnouncementCard {
  title: TranslationKey;
  body: TranslationKey;
  imageSrc: string;
}

@Component({
  selector: "app-new-ankaadia-announcement",
  templateUrl: "./new-ankaadia-announcement.component.html",
  styleUrl: "./new-ankaadia-announcement.component.scss",
  imports: [
    TranslateDirective,
    PrimeTemplate,
    CarouselModule,
    ConfirmDialogModule,
    DialogModule,
    SafePipe,
    NewAnkaadiaAnnouncementHeaderComponent,
    Button,
  ],
})
export class NewAnkaadiaAnnouncementComponent {
  protected readonly newAnkaadiaConfirmationKey = "newAnkaadiaConfirmationKey";
  protected readonly isNil = isNil;
  protected isAnimating = false;
  protected activeIndex = 0;

  protected data: AnnouncementCard[] = [
    {
      title: "newAnkaadiaAnnouncement.sections.ui.title",
      body: "newAnkaadiaAnnouncement.sections.ui.body",
      imageSrc: "/assets/new-ankaadia-announcement/ui-section.svg",
    },
    {
      title: "newAnkaadiaAnnouncement.sections.dashboard.title",
      body: "newAnkaadiaAnnouncement.sections.dashboard.body",
      imageSrc: "/assets/new-ankaadia-announcement/dashboard-section.svg",
    },
    {
      title: "newAnkaadiaAnnouncement.sections.menu.title",
      body: "newAnkaadiaAnnouncement.sections.menu.body",
      imageSrc: "/assets/new-ankaadia-announcement/menu-section.svg",
    },
    {
      title: "newAnkaadiaAnnouncement.sections.search.title",
      body: "newAnkaadiaAnnouncement.sections.search.body",
      imageSrc: "/assets/new-ankaadia-announcement/search-section.svg",
    },
  ];

  protected readonly visible = input(false);
  protected readonly visibleChange = output<boolean>();

  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    private readonly destroyRef: DestroyRef,
    private readonly settings: SettingsService,
    private readonly specifics: OrganizationFactoryService,
    private readonly service: UserEnvironmentService,
    private readonly userEnvironmentService: UserEnvironmentService
  ) {
    effect(() => {
      if (!this.visible()) {
        return;
      }

      this.userEnvironmentService
        .userHasSeenNewAnkaadiaAnnouncement()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    });
  }

  protected onPageChange(event: CarouselPageEvent): void {
    this.isAnimating = true;
    setTimeout(() => {
      this.activeIndex = event.page;
      this.isAnimating = false;
    }, 150);
  }

  protected openNewAnkaadiaUi(event: MouseEvent): void {
    this.service.switchToAlternativeEnvironment(this.newAnkaadiaConfirmationKey, event);
  }

  protected openDocumentation(): void {
    this.specifics
      .getOrganizationSpecifics(this.settings.organizationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((specifics) => specifics.getHelpUrlNewUi())
      )
      .subscribe((url) => this.documentRef.defaultView.open(url, "_blank"));
  }
}
